import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ScheduleResponse } from '@shared/models/campaignsInterfaces.model';
import { StatsPerScenario } from '@shared/models/statsPerScenario.model';
import { Observable } from 'rxjs';
import { urlEndPoints } from '../config/end-points';

@Injectable({
  providedIn: 'root',
})
export class CampaignsDal {
  constructor(private httpClient: HttpClient) {}

  getCampaigns(
    page: number | null,
    limit: number | null,
    accessOptionPositions: string,
    fromAccount?: boolean
  ): Observable<any> {
    return this.httpClient.get<any>(
      `${urlEndPoints.campaigns}` +
        '?page=' +
        page +
        '&limit=' +
        limit +
        '&positions=' +
        accessOptionPositions +
        (fromAccount ? '&fromAccount=true' : '')
    );
  }

  getAllCampaigns(): Observable<any> {
    return this.httpClient.get<any>(`${urlEndPoints.campaigns}all`);
  }

  getAllCampaignRequests(campaignId: string, scenarioId: string, fromDate: string, toDate: string): Observable<any> {
    return this.httpClient.get<any>(
      `${urlEndPoints.campaigns}getAllCampaignRequests` +
        '?campaignId=' +
        campaignId +
        '&scenarioId=' +
        scenarioId +
        '&fromDate=' +
        fromDate +
        '&toDate=' +
        toDate
    );
  }

  getCampaignRequests(
    page: number,
    limit: number,
    campaignId: string,
    profile?: string,
    onHold?: boolean,
    searchValue?: string,
    isNight?: boolean
  ): Observable<any> {
    return this.httpClient.get<any>(
      `${urlEndPoints.campaigns}requests/${campaignId}` +
        '?page=' +
        page +
        '&limit=' +
        limit +
        (profile ? '&profile=' + profile : '') +
        (onHold ? '&onHold=' + onHold : '') +
        (searchValue ? '&searchValue=' + searchValue : '') +
        (isNight ? '&isNight=' + isNight : '')
    );
  }

  getCampaignConstraints(
    page: number,
    limit: number,
    campaignId: string,
    constraintType: string,
    searchValue?: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      `${urlEndPoints.campaigns}constraints/${campaignId}` +
        '?page=' +
        page +
        '&limit=' +
        limit +
        '&constraintType=' +
        constraintType +
        (searchValue ? '&searchValue=' + searchValue : '')
    );
  }

  getCampaignNeeds(
    page: number,
    limit: number,
    campaignId: string,
    searchValue?: string,
    needsAnesthMode?: string,
    needType?: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      `${urlEndPoints.campaigns}needs/${campaignId}` +
        '?page=' +
        page +
        '&limit=' +
        limit +
        (searchValue ? '&searchValue=' + searchValue : '') +
        (needsAnesthMode ? '&needsAnesthMode=' + needsAnesthMode : '') +
        '&needType=' +
        needType
    );
  }

  checkCreate(
    fromDate: string,
    toDate: string,
    position: string,
    campaignPositions: string[],
    campaignId?: string,
    campaignPeriods?: string
  ): Observable<any> {
    return this.httpClient.post<any>(
      `${urlEndPoints.campaigns}checkCreate/` +
        '?fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        '&position=' +
        position +
        (campaignId ? '&campaignId=' + campaignId : ''),
      { campaignPositions, campaignPeriods }
    );
  }

  generatePlanning(campaignId: string): Observable<any> {
    return this.httpClient.post<any>(`${urlEndPoints.campaigns}generatePlanning`, { campaignId });
  }

  generateCampaignRequests(campaignId: string): Observable<any> {
    return this.httpClient.post<any>(`${urlEndPoints.campaigns}generateCampaignRequests`, { campaignId });
  }

  addCampaign(body: {
    name: string;
    openingDate: string;
    closingDate: string;
    fromDate: string;
    toDate: string;
    numberOfRequestPerPersons: number;
    positions: string[];
    status?: string;
    hospital: string;
  }): Observable<any> {
    return this.httpClient.post<any>(`${urlEndPoints.campaigns}`, body);
  }

  addCampaignRequest(body: {
    date: string;
    requestCampaign: string;
    hospital: string;
    availability: string;
    reason?: string;
    profile?: string;
    reasonMaps: string[];
    period: string;
  }): Observable<any> {
    return this.httpClient.post<any>(`${urlEndPoints.campaigns}request`, body);
  }

  modifyCampaign(id: string, body: any): Observable<any> {
    return this.httpClient.patch<any>(`${urlEndPoints.campaigns}${id}`, body);
  }

  deleteCampaign(id: string): Observable<any> {
    return this.httpClient.delete<any>(`${urlEndPoints.campaigns}${id}`);
  }

  deleteCampaignRequest(id: string): Observable<any> {
    return this.httpClient.delete<any>(`${urlEndPoints.campaigns}request/${id}`);
  }

  getCampaignScenarios(campaignId: string, isAdjustment?: boolean): Observable<StatsPerScenario[]> {
    return this.httpClient.get<StatsPerScenario[]>(
      `${urlEndPoints.campaigns}scenarios/${campaignId}?isAdjustment=${isAdjustment}`
    );
  }

  generateExcelReport(campaignId: string, scenarioId: string, isNotCampaign?: boolean): Observable<ScheduleResponse> {
    return this.httpClient.get<ScheduleResponse>(
      urlEndPoints.campaigns +
        `generateExcelFromScenario?campaignId=${campaignId}&scenarioId=${scenarioId}&isNotCampaign=${isNotCampaign}`
    );
  }

  exportScenario(campaignId: string, scenarioId: string): Observable<any> {
    return this.httpClient.post<any>(
      urlEndPoints.campaigns + `exportScenario?campaignId=${campaignId}&scenarioId=${scenarioId}`,
      {}
    );
  }

  getScenarios(hospital: string): Observable<any> {
    return this.httpClient.get<any>(urlEndPoints.campaigns + `allScenarios?hospitalId=${hospital}`);
  }

  checkPrerequisites(campaignId: string): Observable<any> {
    return this.httpClient.get<any>(urlEndPoints.campaigns + `checkPrerequisites?campaignId=${campaignId}`);
  }

  getCampaign(campaignId: string): Observable<any> {
    return this.httpClient.get<any>(urlEndPoints.campaigns + `getCampaign?campaignId=${campaignId}`);
  }

  getUnmetCampaignNeeds(campaignId: string, scenario: string): Observable<any> {
    return this.httpClient.get<any>(`${urlEndPoints.campaigns}unmetNeeds/${campaignId}` + '?scenario=' + scenario);
  }
}
